import React, { Component } from 'react'
import { Link, withRouter } from 'react-router'
import { push } from 'react-router-redux'
import { connect } from 'react-redux'
import isEqual from 'lodash.isequal'
import lscache from 'lscache'
import humanizeList from 'humanize-list'

import classNames from './PlaylistEditor.css'
import { fetchPlaylists, savePlaylist, deletePlaylist } from '../redux/playlists'
import { fetchAlbums } from '../redux/albums'
import { fetchQueues, saveQueue } from '../redux/queues'
import { searchImages } from '../redux/search'
import { showBanner } from '../redux/banners'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorMessage from '../components/ErrorMessage'
import SortableList from '../components/playlist/SortableList'
import SortableBar from '../components/playlist/SortableBar'
import AlbumEditModal from '../components/playlist/AlbumEditModal'
import AlbumPicker from '../components/playlist/AlbumPicker'
import ConstraintsForm from '../components/playlist/ConstraintsForm'
import PlaylistMenu from '../components/playlist/PlaylistMenu'
import ToggleForm from '../components/ToggleForm'
import DiversityPicker from '../components/search/DiversityPicker'
import { getHash, createImageGetter } from '../utils/images'
import { pluralize, formatMinutes } from '../utils/string'
import { mapStateToLists } from '../utils/lists'
import { picassoFetch } from '../utils/fetch'
import { getItemName, isDynamicAlbum } from '../components/playlist//utils'

const STATE_CACHE_KEY = 'playlist-editor-state'

export function clearPlaylistEditorCache () {
  lscache.remove(STATE_CACHE_KEY)
}

class PlaylistEditor extends Component {
  state = {
    name: this.props.playlist ? this.props.playlist.name : '',
    structure: this.props.playlist ? JSON.parse(this.props.playlist.json_data) : { albums: [] },
    editIndex: null,
    conflicts: null,
    saving: false,
    success: false
  }
  componentDidMount () {
    const { params, location, albums, fetchAlbums, removeLocationQuery } = this.props
    if (!albums) fetchAlbums()

    const { lists, fetchPlaylists } = this.props
    if (!lists) fetchPlaylists()

    const { playlistQueues, fetchQueues } = this.props
    if (!playlistQueues) fetchQueues()

    const cache = lscache.get(STATE_CACHE_KEY)
    if (cache && (!params.id || this.props.playlist)) {
      if (location.query.addAlbum) {
        cache.structure.albums.push({ album: location.query.addAlbum })
      }
      this.setState(cache)
      this.fetchCounts(cache.structure)
      clearPlaylistEditorCache()
      removeLocationQuery(params.id)
    } else if (params.id) {
      const { playlist } = this.props
      const { structure } = this.state
      if (playlist && albums) {
        this.fetchCounts(structure)
      }
    }

    this.props.router.setRouteLeaveHook(this.props.route, this.routerWillLeave)
  }
  routerWillLeave = () => {
    if (this.isUnsaved() && !this.preventSaveAlert) {
      return 'Your changes have not been saved. Are you sure you want to leave?'
    }
  }
  isUnsaved = () => {
    const { params, playlist } = this.props
    const { name, structure } = this.state
    if (!name || structure.albums.length === 0) {
      return false
    }
    if (params.id) {
      return (name && name !== playlist.name) || isEqual(structure, JSON.parse(playlist.json_data)) === false
    }
    return true
  }
  componentDidUpdate (prevProps, prevState) {
    const { albums } = this.props
    if (albums) {
      this.fetchCounts(this.state.structure)
    }
  }
  componentWillReceiveProps (nextProps) {
    const { playlist } = this.props
    if ((!playlist && nextProps.playlist) || (playlist && nextProps.playlist && playlist.id !== nextProps.playlist.id)) {
      const structure = JSON.parse(nextProps.playlist.json_data)
      this.setState({
        name: nextProps.playlist.name,
        structure
      })
    }
  }
  fetchCounts (structure) {
    const { entities, search, searchImages } = this.props
    structure.albums.forEach(item => {
      const album = entities.albums[item.album]
      if (album) {
        const images = search[getHash(album.json_data)] || {}
        if (!images.data && !images.fetching && !images.error) {
          searchImages(JSON.parse(album.json_data))
        }
      }
    })
  }
  goBack = () => {
    if (this.props.backToQueueEditor) {
      window.history.back()
    } else {
      this.props.push('/playlists')
    }
  }
  onReorder = (albums) => {
    this.setState({
      success: false,
      conflicts: null,
      structure: {
        ...this.state.structure,
        albums
      }
    })
  }
  updateName = e => {
    this.setState({
      name: e.target.value,
      success: false
    })
  }
  addAlbum = (album = {}) => {
    const { structure } = this.state
    this.updateStructure('albums', structure.albums.concat(album))
  }
  removeAlbum = index => {
    const { albums } = this.state.structure
    this.setState({ conflicts: null })
    this.updateStructure('albums', [
      ...albums.slice(0, index),
      ...albums.slice(index + 1)
    ])
  }
  updateAlbum = (index, key, value) => {
    const { albums } = this.state.structure
    this.updateStructure('albums', [
      ...albums.slice(0, index),
      { ...albums[index], [key]: value },
      ...albums.slice(index + 1)
    ])
  }
  updateStructure = (key, value) => {
    const { structure } = this.state
    this.setState({
      structure: {
        ...structure,
        [key]: value
      },
      success: false
    })
  }
  openEditModal = index => {
    this.setState({
      editIndex: index,
      albumSnapshot: this.state.structure.albums[index]
    })
  }
  closeEditModalAndRestore = () => {
    const { structure, editIndex, albumSnapshot } = this.state
    const { albums } = structure
    this.updateStructure('albums', [
      ...albums.slice(0, editIndex),
      albumSnapshot,
      ...albums.slice(editIndex + 1)
    ])
    this.closeEditModal()
  }
  closeEditModal = () => {
    this.setState({
      editIndex: null
    })
  }
  onCreateNewAlbum = () => {
    this.preventSaveAlert = true
    lscache.set(STATE_CACHE_KEY, this.state)
  }
  previewPlaylist = () => {
    this.setState({ loadingPreview: true })
    const { params, sendToPlayNow, presentChannel } = this.props
    this.savePlaylist().then(() => {
      return sendToPlayNow(undefined, { playlist_id: params.id })
    }).then(data => {
      presentChannel(data.channel)
    }).catch(() => {
      this.setState({ loadingPreview: false })
    })
  }
  savePlaylist = () => {
    const { params, savePlaylist } = this.props
    const { name, structure } = this.state
    const redirect = this.props.playlist ? null : undefined
    this.preventSaveAlert = true
    this.setState({
      conflicts: null,
      saving: true,
      checkingForConflicts: false,
      conflictCheckError: null
    })
    return savePlaylist({ name }, structure, params.id, redirect)
      .then(playlist => {
        this.setState({
          checkingForConflicts: true,
          saving: false,
          success: true
        })
        this.props.showBanner('Playlist saved')
        this.props.checkForConflicts(playlist).then(response => {
          this.setState({
            conflicts: response.conflicts,
            checkingForConflicts: false
          })
        }, e => this.setState({
          checkingForConflicts: false,
          conflictCheckError: e
        }))
      })
  }
  saveAsNew = () => {
    const { playlist, savePlaylist } = this.props
    const { name, structure } = this.state
    const newName = playlist.name === name ? name + ' copy' : name
    this.preventSaveAlert = true
    this.setState({ savingNew: true })
    return savePlaylist({ name: newName }, structure)
      .then(() => {
        this.props.showBanner('New playlist saved')
        this.setState({ savingNew: false })
      })
  }
  deletePlaylist = () => {
    let message = `Are you sure you want to delete ${this.props.playlist.name}?`
    if (this.props.playlistQueues.length !== 0) {
      message += ` It is part of ${humanizeList(this.props.playlistQueues.map(list => list.name))}`
    }
    if (window.confirm(message)) {
      this.preventSaveAlert = true
      this.props.deletePlaylist(this.props.playlist.id)
      this.props.showBanner('Playlist deleted')
    }
  }
  renderAlbum = (album, index, simple) => {
    const { conflicts } = this.state
    const subtitle = (
      <div>
        {this.renderDuration(album)}
        {simple &&
          <Link to={`/album/${album.album}/edit`} className={classNames.textLink}>
            Edit underlying album
          </Link>
        }
        {!simple && conflicts &&
          conflicts.filter(c => c.album === index).map(this.renderConflict)
        }
      </div>
    )
    if (simple) {
      return (
        <SortableBar
          title={getItemName(album, this.props.entities)}
          thumb={this.getAlbumThumb(album)}
          subtitle={subtitle}
          showHandle={false}
          className={classNames.simpleAlbum}
        />
      )
    }
    return (
      <SortableBar
        key={index + album.album}
        title={getItemName(album, this.props.entities)}
        thumb={this.getAlbumThumb(album)}
        subtitle={subtitle}
        onEdit={() => this.openEditModal(index)}
        onRemove={() => this.removeAlbum(index)}
        dotsMenu
      />
    )
  }
  renderHeading (heading, extra) {
    return (
      <div className={classNames.heading}>
        {heading}
        {extra}
      </div>
    )
  }
  renderDuration = item => {
    if (!item.album) {
      return 'Unknown'
    }
    if (isDynamicAlbum(item)) {
      return 'Dynamic'
    }
    const { entities, search } = this.props
    const album = entities.albums[item.album]
    if (!album) {
      return 'This album has been deleted and should be removed from the playlist'
    }
    if (item.presentationLimitImages) {
      return `${pluralize(item.presentationLimitImages, 'image')}, ${this.renderMinutesCount(item, album, item.presentationLimitImages)} (limited)`
    }
    if (item.albumLimit) {
      return `${pluralize(item.albumLimit, 'image')}, ${this.renderMinutesCount(item, album, item.albumLimit)} (limited)`
    }
    const hash = getHash(album.json_data)
    if (!search[hash] || search[hash].fetching) {
      return <LoadingSpinner className={classNames.spinner} style={{ width: '10px', height: '10px', margin: '2px' }} />
    }
    if (search[hash].error) {
      return 'Error retrieving images'
    }
    return `${pluralize(search[hash].count, 'image')}, ${this.renderMinutesCount(item, album, search[hash].count)}`
  }
  getAlbumImageCount = item => {
    if (!item.album) {
      return null
    }
    const { entities, search } = this.props
    const album = entities.albums[item.album]
    if (!album) {
      return null
    }
    if (item.presentationLimitImages && item.albumLimit) {
      return Math.min(item.presentationLimitImages, item.albumLimit)
    }
    if (item.presentationLimitImages || item.albumLimit) {
      return item.presentationLimitImages || item.albumLimit
    }
    const hash = getHash(album.json_data)
    if (!search[hash] || search[hash].fetching || search[hash].error) {
      return null
    }
    return search[hash].count
  }
  getAlbumDuration = item => {
    const count = this.getAlbumImageCount(item)
    if (!count) return 0

    const { entities } = this.props
    const album = entities.albums[item.album]
    if (!album) return 0

    const multiplier = item.repeatTimes ? item.repeatTimes + 1 : 1
    const delay = item.presentationSpeed || album.presentation_speed || 3

    let minutes = (delay * count * multiplier) / 60
    if (item.presentationLimitMinutes) {
      return Math.min(item.presentationLimitMinutes, minutes)
    }
    return minutes
  }
  getAlbumThumb (item) {
    const { entities } = this.props
    const album = entities.albums[item.album]
    if (!album) {
      return null
    }
    return this.props.getImageSource(album.cover)
  }
  renderMinutesCount (item, album, count) {
    const multiplier = item.repeatTimes ? item.repeatTimes + 1 : 1
    const delay = item.presentationSpeed || album.presentation_speed || 3
    // const type = item.presentationType || album.presentation_type || 'kenburns'
    let minutes = (delay * count * multiplier) / 60
    if (item.presentationLimitMinutes) {
      minutes = Math.min(item.presentationLimitMinutes, minutes)
    }
    return formatMinutes(minutes)
  }
  getDefaultSortSuffix (item) {
    if (!item.album || isDynamicAlbum(item)) {
      return ''
    }
    const { entities } = this.props
    const album = entities.albums[item.album]
    const structure = JSON.parse(album.json_data)
    const operator = structure.sort.operator ? `-${structure.sort.operator}` : ''
    return ` (${structure.sort.value}${operator})`
  }
  getAlbumPropertySuffix (item, field) {
    if (!item.album || isDynamicAlbum(item)) return ''
    const { entities } = this.props
    const album = entities.albums[item.album]
    return album[field] ? ` (${album[field]})` : ''
  }
  renderConflict = conflict => {
    return (
      <div className={classNames.conflict}>
        {conflict.conflict}
      </div>
    )
  }
  renderPlaylistConflict = conflict => {
    return (
      <div className={classNames.conflict}>
        {conflict.album !== undefined &&
          'Album at index ' + conflict.album + ': '
        }
        {conflict.conflict}
      </div>
    )
  }
  renderEditModal (album, index) {
    const { params, albums, lists, isOwner } = this.props
    return (
      <AlbumEditModal
        renderAlbum={this.renderAlbum}
        params={params}
        albums={albums}
        lists={lists}
        album={album}
        update={(key, value) => this.updateAlbum(index, key, value)}
        isOwner={isOwner}
        renderHeading={this.renderHeading}
        initialValue={getItemName(album, this.props.entities)}
        duration={this.renderDuration(album)}
        onCancel={this.closeEditModalAndRestore}
        onSave={this.closeEditModal}
        onCreateNewAlbum={this.onCreateNewAlbum}
        defaultSortSuffix={this.getDefaultSortSuffix(album)}
        defaultPresentationSortSuffix={this.getAlbumPropertySuffix(album, 'presentation_sort') || this.getDefaultSortSuffix(album)}
        defaultPresentationTypeSuffix={this.getAlbumPropertySuffix(album, 'presentation_type') || ' (kenburns)'}
        playlistName={this.state.name}
        counts={this.renderCounts([ album ])}
        getImageSource={this.props.getImageSource}
      />
    )
  }
  albumsTotal (albums, fn) {
    return albums.reduce((total, item) => {
      if (total === null) {
        return null
      }
      return total + fn(item)
    }, 0)
  }
  getCheckConflictLabel () {
    if (this.state.saving) {
      return 'Saving playlist…'
    }
    if (this.state.checkingForConflicts) {
      return 'Checking for conflicts…'
    }
    if (this.isUnsaved()) {
      return 'Save and check for conflicts'
    }
    if (this.state.conflicts && this.state.conflicts.length === 0) {
      return 'No conflicts found'
    }
    return 'Check for conflicts'
  }
  renderCounts (albums, limitImages, limitMinutes) {
    const { playlist } = this.props
    let count = this.albumsTotal(albums, this.getAlbumImageCount)
    let limitImagesNote
    let limitMinutesNote
    let limitImagesPercentage
    let limitMinutesPercentage
    if (limitImages && limitImages < count) {
      if (!limitMinutes) {
        // Store percentage of images limited to roughly adjust duration
        limitImagesPercentage = limitImages / count
      }
      count = limitImages
      limitImagesNote = ' (limited)'
    }
    let duration = this.albumsTotal(albums, this.getAlbumDuration)
    if (limitMinutes && limitMinutes < duration) {
      if (!limitImages) {
        // Store percentage of minutes limited to roughly adjust image count
        limitMinutesPercentage = limitMinutes / duration
      }
      duration = limitMinutes
      limitMinutesNote = ' (limited)'
    }
    if (limitImagesPercentage) {
      duration = duration * limitImagesPercentage
      limitMinutesNote = ' (estimate)'
    }
    if (limitMinutesPercentage) {
      count = count * limitMinutesPercentage
      limitImagesNote = ' (estimate)'
    }
    return (
      <div className={classNames.count}>
        <span>
          {pluralize(count, 'photo')}{limitImagesNote}, {formatMinutes(duration)}{limitMinutesNote}
        </span>
        {playlist && playlist.id &&
          <button
            onClick={this.previewPlaylist}
            className={classNames.previewButton}
            disabled={this.state.loadingPreview}>
            {this.state.loadingPreview ? 'Loading…' : 'Preview'}
          </button>
        }
      </div>
    )
  }
  render () {
    const { params, playlist, albums, error, fetchPlaylists } = this.props
    if (!albums) {
      return <LoadingSpinner />
    }
    if (params.id) {
      if (!playlist) {
        return <LoadingSpinner />
      }
      if (error) {
        return <ErrorMessage message='Playlist could not be loaded' onRetry={fetchPlaylists} />
      }
    }
    const { name, structure, editIndex, conflicts } = this.state
    return (
      <div className={classNames.page}>
        {params.id &&
          <p className={classNames.intro}>
            Modifying a playlist will affect all queues that contain this playlist.
          </p>
        }
        {this.renderHeading('Playlist name')}
        <input
          type='text'
          value={name}
          onChange={this.updateName}
          autoFocus={!params.id}
        />
        {this.renderHeading('Albums')}
        <SortableList
          items={structure.albums}
          renderItem={this.renderAlbum}
          sortHandle='handle'
          onSort={this.onReorder}
        />
        <div className={structure.albums.length !== 0 ? classNames.pickerWrapper : classNames.pickerWrapperNoAlbums}>
          <AlbumPicker
            albums={albums}
            lists={this.props.lists}
            isOwner={this.props.isOwner}
            onChange={album => this.addAlbum({ album })}
            resetOnChange
            collapseOnSelect
            onCreateNewAlbum={this.onCreateNewAlbum}
            createNewAlbumLink
            playlistID={params.id}
            getImageSource={this.props.getImageSource}
            placeholder='Add an album to playlist'
          />
        </div>
        {structure.albums.length !== 0 &&
          this.renderCounts(structure.albums, structure.presentationLimitImages, structure.presentationLimitMinutes)
        }
        {this.renderHeading('Playlist options')}
        <ToggleForm
          showLabel='Apply a limit or repeat'
          description='Limits will ensure the playlist is played only up to a certain number of images or minutes.'
          indicatorValues={[ structure.presentationLimitImages, structure.presentationLimitMinutes ]}>
          {this.renderHeading('Limit playlist to number of photos')}
          <input
            type='number'
            value={structure.presentationLimitImages || ''}
            min={1}
            className={classNames.numberInput}
            onChange={e => this.updateStructure('presentationLimitImages', parseInt(e.target.value) || undefined)}
          />
          {this.renderHeading('Limit playlist to number of minutes')}
          <input
            type='number'
            value={structure.presentationLimitMinutes || ''}
            min={0.1}
            className={classNames.numberInput}
            onChange={e => this.updateStructure('presentationLimitMinutes', parseFloat(e.target.value) || undefined)}
          />
          {this.renderHeading('Repeat number of times')}
          <input
            type='number'
            value={structure.repeat || ''}
            min={1}
            className={classNames.numberInput}
            onChange={e => this.updateStructure('repeat', parseInt(e.target.value) || undefined)}
          />
          <DiversityPicker
            value={structure.diversity || ''}
            onChange={e => this.updateStructure('diversity', parseFloat(e.target.value) || undefined)}
          />
        </ToggleForm>
        <label>
          <input
            type='checkbox'
            checked={structure.allow_duplicates}
            onChange={() => this.updateStructure('allow_duplicates', !structure.allow_duplicates)}
          /> Allow duplicate images
        </label>
        <ConstraintsForm
          values={structure}
          update={this.updateStructure}
          renderHeading={this.renderHeading}
          title='Schedule this playlist (optional)'
          showLocationPicker
          hideAlbumLimit
          userDefaultLocation={this.props.userDefaultLocation}
        />
        {conflicts && conflicts.length !== 0 && this.renderHeading('Playlist conflicts')}
        {conflicts && conflicts.map(this.renderPlaylistConflict)}
        <div className={classNames.buttons}>
          {this.props.playlist &&
            <button
              onClick={this.deletePlaylist}
              className={classNames.deleteButton}>
              Delete Playlist
            </button>
          }
          {!this.state.saving &&
            <button onClick={this.goBack} className={classNames.cancelButton}>
              Cancel
            </button>
          }
          {!this.state.saving &&
            <button
              onClick={this.saveAsNew}
              className={classNames.cancelButton}
              disabled={this.state.savingNew || this.state.saving}>
              {this.state.savingNew ? 'Saving…' : 'Save as new'}
            </button>
          }
          {!this.state.success &&
            <button
              onClick={this.savePlaylist}
              className={classNames.saveButton}
              disabled={this.state.saving || this.state.savingNew || !this.isUnsaved()}>
              {this.state.saving ? 'Saving…' : 'Save'}
            </button>
          }
          {this.state.success &&
            <button
              onClick={this.goBack}
              className={classNames.saveButton}>
              Done
            </button>
          }
          {playlist &&
            <div>
              <button
                onClick={this.savePlaylist}
                disabled={this.state.checkingForConflicts || this.state.saving || structure.albums.length === 0 || !name}
                className={classNames.conflictCheckButton}>
                {this.getCheckConflictLabel()}
              </button>
              {this.state.conflictCheckError &&
                <div><small>There was an error checking for conflicts</small></div>
              }
            </div>
          }
        </div>
        <PlaylistMenu
          id={playlist && playlist.id}
          structure={structure}
          playlistName={name}
          {...this.props}
        />
        {editIndex !== null &&
          this.renderEditModal(structure.albums[editIndex], editIndex)
        }
      </div>
    )
  }
}

const mapStateToProps = ({ entities, albums, auth, playlists, search, queues, nav }, { params }) => ({
  entities,
  search,
  playlist: params.id ? entities.playlists[params.id] : null,
  error: playlists.error,
  albums: albums.all.data && albums.all.data.map(id => entities.albums[id]),
  lists: mapStateToLists(playlists, entities),
  isOwner: album => album.user_id === auth.user.id,
  submitting: playlists.submitting,
  submitError: playlists.submitError,
  getImageSource: createImageGetter(auth),
  backToQueueEditor: /\/queues\/.+/.test(nav.previousPath),
  // for PlaylistMenu:
  fetchChannels: () => picassoFetch(auth, 'list_channels')
    .then(data => data.channels.filter(c => parseInt(c.owner_id) === auth.user.id)),
  sendToPlayNext: (channelID, body) => picassoFetch(auth, 'add_playlist', {
    channel_id: channelID,
    ...body
  }),
  sendToPlayNow: (channelID, body) => picassoFetch(auth, 'play_playlist', {
    channel_id: channelID,
    ...body
  }),
  checkForConflicts: playlist => picassoFetch(auth, 'validate_playlist', { playlist_id: playlist.id }),
  userDefaultLocation: auth.user.location_address || auth.user.location,
  playlistQueues: queues.data && queues.data
    .map(id => entities.queues[id])
    .filter(queue => JSON.parse(queue.json_data).playlists.indexOf(params.id) !== -1)
})

const mapDispatchToProps = {
  push,
  fetchAlbums,
  fetchPlaylists,
  savePlaylist,
  deletePlaylist,
  searchImages,
  showBanner,
  removeLocationQuery: id => dispatch => dispatch(push(`/playlists/${id || 'create'}`)),
  // for PlaylistMenu:
  fetchQueues,
  saveQueue,
  presentChannel: id => dispatch => dispatch(push(`/present/${id}`))
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(PlaylistEditor))



// WEBPACK FOOTER //
// ./src/pages/PlaylistEditor.js